import type * as THREE from 'three';
import { CLIFF_SIGHT_STEP } from '../../../shared/game/cliffs.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import type { SceneryBatches } from './scenery-batches.js';

const tones = [0x6a655c, 0x7d7262, 0x625d57, 0x857965];

/**
 * Cliffs are the ground itself, shaded as rock where it is too steep to walk (`coast-visual.ts`). This only breaks up their faces
 * with a few lumps of rock and drops fallen stones at their feet; collision stays with the derived cliff obstacles.
 */
export function dressCliffs(map: MapDefinition, random: () => number, batches: SceneryBatches, rock: THREE.BufferGeometry, material: THREE.Material) {
  for (const cliff of map.obstacles) {
    if (cliff.kind !== 'cliff' || (cliff.height ?? 0) < CLIFF_SIGHT_STEP) continue;
    const height = cliff.height!, rim = cliff.rim ?? 0, perimeter = cliff.width + cliff.depth;
    // About one ledge per 90 m² of face, set on the face itself: across the steep band the ground climbs the whole height.
    // Broad, shallow slabs read as the rock's bedding rather than loose cobbles stuck to it.
    // Bands are often a cell or two wide, so the count is drawn rather than rounded up.
    for (let i = 0, count = Math.floor(Math.max(cliff.width, cliff.depth) * height / 90 + random()); i < count; i++) {
      const x = cliff.x + (random() - .5) * cliff.width, z = cliff.z + (random() - .5) * cliff.depth, size = .35 + random() * .35;
      batches.add('cliff-lump', rock, material, x, 0, z, size * 1.5, size * .6, size * 1.15, random() * 6, tones[i % 3 === 0 ? 0 : 2]);
    }
    for (let i = 0, count = Math.floor(perimeter / 3 + random()); i < count; i++) {
      // A point just outside the steep band, kept only on its low side.
      const along = random() - .5, side = random() < .5, flip = random() < .5 ? -1 : 1;
      const x = cliff.x + (side ? along * cliff.width : flip * (cliff.width / 2 + .35)), z = cliff.z + (side ? flip * (cliff.depth / 2 + .35) : along * cliff.depth);
      if (terrainHeight(map, x, z) > rim - height * .6) continue;
      const size = .15 + random() * .35;
      batches.add('cliff-scree', rock, material, x, size * .25, z, size * 1.2, size * .65, size, random() * 6, tones[(i + 1) % tones.length]);
    }
  }
}
